import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {v4 as uuid} from 'uuid';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import { SUBTABS, CHOOSE_STEPS } from './ConstPaths';
import TestDriveChooseFirst from './TestDriveChooseFirst';
import TestDriveChooseMethod from './TestDriveChooseMethod';
import { getAccessMethodNameForID, getAccessMethodCategoryForID, getAccessMethodSelectionMethodsForID } from './ConstAccessMethodsMock';

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  isLowTechPath: PropTypes.bool,
  hasCurrentMethod: PropTypes.string,
  firstMethodCategory: PropTypes.string,
  updateActiveTestDriveId: PropTypes.func.isRequired,
  activeTestDrive: PropTypes.object,
  updateSession: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  isLowTechPath: false,
  hasCurrentMethod: '',
  firstMethodCategory: '',
  activeTestDrive: {}
};

//Manage the wizard form for the Test-drive / Choose sub-tab
//Initialize the forms with props and manage those as local state.
// Following pattern described in https://goshacmd.com/wizard-form-collect-info/

//the steps of the wizard form:
const steps = CHOOSE_STEPS;

const BASE_URL = SUBTABS.CHOOSE + "/";
const NEXT_SECTION = SUBTABS.SETUP;

class TestDriveChoose extends React.Component {
  constructor(props) {
    super(props);

    //manage the incoming props  - those that can be changed in this section - as local state

    //TD object experiment
    this.state = {
      activeTestDrive: this.props.activeTestDrive,
      startNewTestDrive: 'false', //local state for whether to start a new Test-drive (relevant when you visit /try/choose but already have an activeTestDriveId)
      firstMethodCategory: this.props.firstMethodCategory,
      activeAccessMethodID: this.props.activeTestDrive.activeAccessMethodID,
      prevActiveAccessMethodID: this.props.activeTestDrive.activeAccessMethodID
    };

    this.saveChooseFirstDataToSession = this.saveChooseFirstDataToSession.bind(this);
    this.saveFirstMethodCategoryToSession = this.saveFirstMethodCategoryToSession.bind(this);
    //we choose the access method ID in TestDriveChooseMethod,
    //then retrieve and save all the needed properties from that access method profile.
    this.saveActiveAccessMethodProfileToSession = this.saveActiveAccessMethodProfileToSession.bind(this);

    this.onContinue = this.onContinue.bind(this);
  }

  //Get the current step from the URL
  getCurrentStep(){
    //e.g. URL of '/gather-info/contexts/intro' would have step of 'intro'
    //props.match: {"step":"intro"}
    const stepParam = this.props.match.params.step;
    if(stepParam){
      return(stepParam);
    }
    else{
      return(steps.FIRST);
    }
  }

// 1 Do a test-drive, say with 'Touchscreen' access method
// 2 /interpret/choose asks if you want to 'keep exploring solutions'. Say Yes.
// 3 This routes to /choose/method as expected, but it still showed 'Touchscreen' as the access method.
//Using getDerivedStateFromProps hook fixes this so /choose/method will have no access methods selected.
  static getDerivedStateFromProps(props, state) {
    console.log("called getDerivedStateFromProps, props: " + JSON.stringify(props) + ", state: " + JSON.stringify(state));
    //https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
    // Any time the accessMethodID prop changes,
    // Reset any parts of state that are tied to that prop.
    // This occurs when you jump from /interpret/choose to /choose/method: the first render occurs before the accessMethodID has been reset.
    if ((props.activeTestDrive.activeAccessMethodID !== state.prevActiveAccessMethodID))
      {
      return {
        prevActiveAccessMethodID: props.activeTestDrive.activeAccessMethodID,
        activeAccessMethodID: props.activeTestDrive.activeAccessMethodID
      };
    }
    return null;
  }

  //On Continue for a given step:
  // figure out the next step and go to it
  // and if we're totally done with this section, submit the data.
  onContinue() {
    let history = this.props.history;
    let location = this.props.location;
    let nextStep = '';
    let nextRoute = '';
    let goToNextSection = false;
    const curURL = new URL(window.location.href);


    switch(this.getCurrentStep()){
      case steps.FIRST:
        nextStep = steps.ACCESS_METHOD;
        break;
      case steps.ACCESS_METHOD:
        nextStep = '';
        goToNextSection = true;
        break;
      default:
        console.log("   default next step");
        nextStep = steps.FIRST;
    }
    if (!goToNextSection) {
      nextRoute = BASE_URL + nextStep;
    } 
    else {
      // alert("Done with this section. Data saved are: " + JSON.stringify(this.state));
      nextRoute = NEXT_SECTION;
    }
    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  //called on transition from TestDriveChooseFirst, to update global state based on UI interaction there
  saveChooseFirstDataToSession(){

    if( !this.props.isActiveTestDrive){
      this.saveFirstMethodCategoryToSession();
    }
    else{
      if(this.state.startNewTestDrive === 'true'){
        this.saveNewTestDriveIdToSession();
      }
    }
  }

  // Replace the current activeTestDriveId with a new one.
  // this is only called when isActiveTestDrive (i.e., we know there is an activeTestDriveId),
  //so we don't need to check for that here.
  saveNewTestDriveIdToSession(){
    //activeTestDriveId isn't part of activeTestDrive, so we set it directly
    this.props.updateSession('activeTestDriveId', uuid());
    this.props.updateSession('tempMessage', {
      icon:'success',
      title:'Starting a new Test-drive',
      label: '',
      message: 'Previous Test-drive has been saved',
    });
  }

  saveFirstMethodCategoryToSession(){
    this.props.updateActiveTestDriveId();
    //firstMethodCategory isn't part of activeTestDrive, so we set it directly
    this.props.updateSession('firstMethodCategory', this.state.firstMethodCategory);
  }

  //Pulls the other access method properties from the profile, and saves them.
  saveActiveAccessMethodProfileToSession(){
    this.props.updateActiveTestDriveId();
    //TD object experiment
    //the ID is what is maintained by TestDriveChooseMethod
    const activeAccessMethodID = this.state.activeTestDrive.activeAccessMethodID;
    //then we get the other pieces from that ID's profile
    const methodName = getAccessMethodNameForID(activeAccessMethodID);
    const methodCategory = getAccessMethodCategoryForID(activeAccessMethodID);
    const methodSelectionMethods = getAccessMethodSelectionMethodsForID(activeAccessMethodID);

    //we don't need to update localState with those pieces (they aren't part of a TestDriveChoose view)
    //so we can just put them into the activeTestDrive and set App.activeTestDrive
    let newActiveTestDrive = {
      ...this.state.activeTestDrive,
      activeAccessMethodID: activeAccessMethodID,
      activeAccessMethodName: methodName,
      activeAccessMethodCategory: methodCategory,
      activeAccessMethodSelectionMethods: methodSelectionMethods
    }
    //Brute force adjust the active access method setup, too - the properties that depend on activeAccessMethod.
    //so far that's only the activeSetupSelectionMethod
    //Reset it if the access method was cleared
    if (!activeAccessMethodID){
      //TD object experiment
      // this.props.updateSession('activeSetupSelectionMethod', "");
      newActiveTestDrive = {
        ...newActiveTestDrive,
        activeSetupSelectionMethod: ""
      }
    }
    //set it to single value automatically, if not on the lowTechPath
    else if (!this.props.isLowTechPath && (Array.isArray(methodSelectionMethods) && methodSelectionMethods.length === 1)){
      //TD object experiment
      // this.props.updateSession('activeSetupSelectionMethod', methodSelectionMethods[0]);
      newActiveTestDrive = {
        ...newActiveTestDrive,
        activeSetupSelectionMethod: methodSelectionMethods[0]
      }
    }
    this.props.updateSession('activeTestDrive', newActiveTestDrive);
  }

  //the keys are the names of the local state variables, defined above in the constructor
  updateLocalState(key) {
    return evt => {
      const target = evt.target; /* which input has been changed */
      let value = target.type === 'checkbox' ? target.checked: target.value;

      //the chosen card ID is embedded in the ID of the Choose button
      if( key==='activeAccessMethodID'){
        value = this.extractCardID(target.id);
        //clear the access method if it is already selected
        if(value===this.state.activeTestDrive.activeAccessMethodID){
          value="";
        }
        this.props.updateSession('tempMessage', {
          icon:'success',
          title:'Access method changed',
          label: 'Your access method:',
          message: (value) ? getAccessMethodNameForID(value) : 'None selected'
        });
      }

      console.log("Updating TestDriveChoose state with key: " + key + " value: " + value);
      if((key === 'firstMethodCategory') || (key === 'startNewTestDrive')){
        this.setState({ [key]: value });
      }
      else{
        this.setState({
          activeTestDrive: {
            ...this.state.activeTestDrive,
            [key]: value
          }
        });
      }
    }
  }

  //button ID is "button-[cardID string]"
  extractCardID(btnID){
    let btnIdStr = btnID.slice("button-".length);
    return(btnIdStr);
  }
  
  //If new props come in after rendering, update the views in this tab.
  //(This occurs when you refresh a view and it pulls data from the database. 
  // e.g. you stay logged in, it pulls the data on refresh, and displays that data on the refreshed view)
  //Without this, it will not display the data on the refreshed view, even though it has pulled it correctly.
  componentDidUpdate(prevProps, prevState){
    //for simple strings
    if (this.props.firstMethodCategory !== prevProps.firstMethodCategory){
        console.log("--incoming props: " + this.props.firstMethodCategory + " differ from prev props: " + prevProps.firstMethodCategory);
        this.setState({'firstMethodCategory': this.props.firstMethodCategory});
    }
    //for values that are Objects:
    if (_.isEqual(this.props.activeTestDrive, prevProps.activeTestDrive) === false){
      console.log("--incoming activeTestDrive: " + this.props.activeTestDrive + " differ from prev activeTestDrive: " + prevProps.activeTestDrive);
      this.setState({'activeTestDrive': this.props.activeTestDrive});
    };
  }
 
  render() {
    console.log("rendering TestDriveChoose, startNewTestDrive: " + this.state.startNewTestDrive);
    const firstName = this.props.firstName;
    const pronouns = this.props.pronouns;
    const isActiveTestDrive = this.props.isActiveTestDrive;
    const isFirstRouteActive = this.props.isFirstRouteActive;
    const isLowTechPath = this.props.isLowTechPath;
    const hasCurrentMethod = this.props.hasCurrentMethod;
    const curURL = new URL(window.location.href);

    const searchQuery = curURL.search;
    const dev = this.props.dev;

    //whenn rendering these form views, flatten out the activeTestDrive so that the forms don't have to manage that
    return(
    <>
    <Switch>
      <Route path={BASE_URL+ steps.FIRST}>
        <TestDriveChooseFirst
              key={steps.FIRST}
              firstName={firstName}
              pronouns={pronouns}
              isActiveTestDrive={isActiveTestDrive}
              isLowTechPath={isLowTechPath}
              hasCurrentMethod={hasCurrentMethod}
              onContinue={this.onContinue}
              startNewTestDrive={this.state.startNewTestDrive}
              onChangeStartNewTestDrive={this.updateLocalState("startNewTestDrive")}
              firstMethodCategory={this.state.firstMethodCategory}
              onChangeFirstMethodCategory={this.updateLocalState("firstMethodCategory")}
              saveFormData={this.saveChooseFirstDataToSession}
              dev={dev}
            />
      </Route>
      <Route path={BASE_URL + steps.ACCESS_METHOD}>
        <TestDriveChooseMethod
              key={steps.ACCESS_METHOD}
              firstName={firstName}
              pronouns={pronouns}
              isLowTechPath={isLowTechPath}
              firstMethodCategory={this.state.firstMethodCategory}
              onContinue={this.onContinue}
              activeAccessMethodID={this.state.activeTestDrive.activeAccessMethodID}
              onChangeActiveAccessMethodID={this.updateLocalState("activeAccessMethodID")}
              saveFormData={this.saveActiveAccessMethodProfileToSession}
              dev={dev}
            />
      </Route>
      <Route path={BASE_URL}>
        {isFirstRouteActive
          ?
          <Redirect to={BASE_URL + steps.FIRST + searchQuery} />
          :
          <Redirect to={BASE_URL + steps.ACCESS_METHOD + searchQuery} />
        }
      </Route>
    </Switch>
    </>
    );
  }
}

TestDriveChoose.propTypes = propTypes;
TestDriveChoose.defaultProps = defaultProps;

// withRouter wrapper provides access to the match, location, and history props (used in onContinue)
export default withRouter(TestDriveChoose);
